import type { RoomMode } from "./game";

export const ACTIVITY_MODES = ["relic-run", "picture-detectives", "vault-breakers", "planet-hoppers", "music-class"] as const;
export type ActivityMode = (typeof ACTIVITY_MODES)[number];
export type ActivitySummary = {
    id: string;
    mode: ActivityMode;
    title: string;
    hostedAt: number;
    roomCode: string;
    playerCount: number;
    hasSource: number;
};
export type ActivityPlayerResult = {
    sessionId: string; name: string; placement: number | null; score: number | null; elapsedMs: number | null;
};
export type ActivityDetail = ActivitySummary & { sourceText: string; playerResults: ActivityPlayerResult[] };
export type ActivityCalendarDay = { day: string; modes: ActivityMode[] };
export type ActivityMonth = { accountId: string; items: ActivitySummary[]; earliest: number | null; latest: number | null; calendar?: ActivityCalendarDay[] };
export const isActivityMode = (value: unknown): value is ActivityMode => ACTIVITY_MODES.includes(value as ActivityMode);
export const isGameActivity = (mode: ActivityMode): mode is RoomMode => mode !== "music-class";

// Use local calendar boundaries, including offset changes, rather than UTC date slices.
export const activityDayKey = (timestamp: number): string => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
export const activityMonthRange = (year: number, month: number) => ({
    from: new Date(year, month, 1).getTime(), to: new Date(year, month + 1, 1).getTime()
});
export function buildActivityCalendar(items: Iterable<Pick<ActivitySummary, "mode" | "hostedAt">>, timeZone: string): ActivityCalendarDay[] {
    const formatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
    const days = new Map<string, Set<ActivityMode>>();
    for (const item of items) {
        const parts = formatter.formatToParts(item.hostedAt);
        const part = (type: string) => parts.find(value => value.type === type)!.value;
        const key = `${part("year")}-${part("month")}-${part("day")}`;
        if (!days.has(key)) days.set(key, new Set());
        days.get(key)!.add(item.mode);
    }
    return [...days].map(([day, modes]) => ({ day, modes: ACTIVITY_MODES.filter(mode => modes.has(mode)) }));
}
export const groupDayActivities = (items: readonly ActivitySummary[]): Map<ActivityMode, ActivitySummary[]> => {
    const groups = new Map<ActivityMode, ActivitySummary[]>();
    for (const mode of ACTIVITY_MODES) {
        const runs = items.filter(item => item.mode === mode).sort((a, b) => a.hostedAt - b.hostedAt);
        if (runs.length) groups.set(mode, runs);
    }
    return groups;
};
